import { useCallback } from "react";

import { SignupKeysService, type SignupKeyOut } from "@/api";
import { useMutation } from "@/hooks/use-api";
import { parseApiError } from "@/lib/api-error";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Props {
  invite: SignupKeyOut | null;
  onClose: () => void;
  onRevoked: () => void;
}

/** Confirm-by-name dialog for revoking a signup key. Reads-only —
 *  there's no soft-revoke / undo flow. */
export function InviteRevokeDialog({ invite, onClose, onRevoked }: Props) {
  const mutator = useCallback(
    (id: string) => SignupKeysService.revoke(id),
    []
  );
  const { mutate, isLoading, error, reset } = useMutation<string, void>(
    mutator,
    {
      onSuccess: () => {
        onRevoked();
      },
    }
  );

  if (!invite) return null;

  const handleConfirm = async () => {
    await mutate(invite.id);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open onClose={handleClose} size="max-w-md">
      <DialogHeader>
        <DialogTitle>Revoke signup key</DialogTitle>
        <DialogDescription>
          Setelah revoke, kode tidak dapat dipakai untuk registrasi. Tidak
          ada flow untuk undo — buat signup key baru jika diperlukan.
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        <div className="rounded-md border border-ocean-100 bg-ocean-50/40 p-3 text-sm">
          <div className="label-eyebrow mb-1">Prefix kode</div>
          <code className="font-mono text-ocean-900">{invite.display_prefix}…</code>
          {invite.note && (
            <div className="mt-2 text-xs text-neutral-600">
              <span className="font-medium text-neutral-800">Catatan:</span>{" "}
              {invite.note}
            </div>
          )}
        </div>

        {error && (
          <Alert variant="danger" className="mt-3">
            {parseApiError(error)}
          </Alert>
        )}
      </DialogBody>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={isLoading}
        >
          Batal
        </Button>
        <Button
          type="button"
          variant="danger"
          onClick={handleConfirm}
          disabled={isLoading}
        >
          {isLoading ? "Memproses..." : "Revoke signup key"}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
